import { jwtDecode } from "jwt-decode";

import store from "../store";
import { warn } from "./messages";
import { intl } from "../containers/app";
import { handleError } from "./errorLogger";
import { patchEntity } from "./crudOperations";
import { getAccessToken } from "./redux/session/reducer";
import { cleanSession, setSession } from "./redux/session/action";
import { loginUserToMainSite, logOutUserFromMainSite } from "./storage";
import { post, checkHttpStatus, checkResponseGet } from "./httpFunctions";

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

type Credentials = {
  username: string;
  password: string;
};

export interface TokenDto {
  foo: string;
  exp: number;
  iat: number;
}

const customerService = "customer-service",
  customerServiceDocs = "customer-service-docs",
  customerServiceLogin = "customer-service-login";
/**
 * název rolí uživatelů
 */
export const roleNames = {
  superAdmin: "super-admin",
  admin: "admin",
  documents: "documents",
  user: "user",
  superReader: "super-reader",
  reader: "reader",
  customerService,
  customerServiceDocs,
  customerServiceLogin,
  customerServiceRoles: [
    customerService,
    customerServiceDocs,
    customerServiceLogin,
  ],
};

/**
 *  validuje session
 * @param session aktuální session
 */
export async function validateSession(session?: string): Promise<boolean> {
  const loadedSession: string = session || getAccessToken();
  if (loadedSession) {
    const jwt = jwtDecode<TokenDto>(loadedSession);
    const currentTime = Date.now().valueOf() / 1000; //UTC time!
    if (jwt.exp < currentTime) {
      setTimeout(
        () =>
          warn(
            intl.formatMessage({
              id: "session.securityLogout",
              defaultMessage: "Došlo k bezpečnostnímu odhlášení!",
            }),
          ),
        1000,
      ); //zavolani se musi posunout, aby se stacil nacist spravny jazyk uzivatele
      signOut();
      return false;
    } else {
      return true;
    }
  } else {
    return false;
  }
}

/**
 * přihlášeni uživatele
 * @param {Credentials} credentials přihlašovací údaje
 */
export async function signIn(credentials: Credentials): Promise<any> {
  try {
    const response = await (credentials
      ? post("auth", credentials, false)
      : post("auth/login", {}, true, undefined, undefined, undefined, false));
    return await processSignIn(response);
  } catch (err) {
    const error = err as any;
    handleError(error);
    return { success: false };
  }
}

export async function processSignIn(response: any) {
  const { data } = response;
  const success =
    checkHttpStatus(response) &&
    checkResponseGet(response) &&
    !data.data.shouldChangePassword;
  if (success) {
    loginUserToMainSite(data.data);
    await saveUserSession(data.data);
  }
  return { data, success };
}

/**
 * odhlášeni uživatele
 */
export async function signOutApi(): Promise<any> {
  try {
    const response = await post("auth/logout", {});
    return { success: checkHttpStatus(response) };
  } catch (err) {
    const error = err as any;
    handleError(error);
    return { success: false };
  }
}

/**
 * uloží session uživatele
 * @param data data ze serveru
 * @param success jestli uložit uživatele
 */
export async function saveUserSession(data: any, success = true): Promise<any> {
  success && store.dispatch(setSession(data));
}

export async function processSignOut() {
  const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
  const promise = new Promise(logOutUserFromMainSite);
  await Promise.race([timeout, promise]);

  store.dispatch(cleanSession());
}

/**
 * odhlášeni uživatele
 */
export async function signOut(useApi: boolean = true): Promise<void> {
  useApi && (await signOutApi());

  await processSignOut();
}

/**
 * nastaví poslední lokaci uživatele před nuceným přechodem na přihlášení
 * @param path lokace
 */
export function setLastPath(path: string): void {
  sessionStorage.setItem("last-path", path);
}

/**
 * vrátí poslední lokaci uživatele před odhlášením a zároveň ji smaže
 */
export function getLastPath(): string {
  const path = sessionStorage.getItem("last-path") || "/";
  setLastPath("/");
  return path;
}

export function isUser(userRoleName: string): boolean {
  return userRoleName?.toLowerCase() === roleNames.user;
}

/**
 * určí, jestli je uživatel admin nebo super-admin
 * @param userRoleName název uživatelské role
 */
export function isUserAdmin(userRoleName: string): boolean {
  return userRoleName.toLowerCase() === roleNames.admin;
}

/**
 * určí, jestli je uživatel reader nebo super-reader nebo customer-service
 * @param userRoleName název uživatelské role
 * @param includeCs jestli brat v potaz i roli customer-service
 */
export function isUserReader(userRoleName: string, includeCs = true): boolean {
  return (
    userRoleName.toLowerCase().indexOf(roleNames.reader) !== -1 ||
    (includeCs && roleNames.customerServiceRoles.includes(userRoleName))
  );
}

/**
 * jestli je uzivatel DHL
 * @param userRoleName název uživatelské role
 */
export function isSuperUser(userRoleName: string): boolean {
  userRoleName = userRoleName?.toLowerCase();
  return (
    userRoleName === roleNames.superAdmin ||
    userRoleName === roleNames.superReader ||
    userRoleName === roleNames.reader ||
    roleNames.customerServiceRoles.includes(userRoleName)
  );
}

export function isSuperAdmin(userRoleName: string): boolean {
  userRoleName = userRoleName?.toLowerCase();
  return userRoleName === roleNames.superAdmin;
}

export function isDocsAdminUser(userRoleName: string): boolean {
  userRoleName = userRoleName?.toLowerCase();
  return (
    userRoleName === roleNames.customerServiceDocs ||
    userRoleName === roleNames.customerServiceLogin ||
    userRoleName === roleNames.superAdmin
  );
}

export function isLoginUser(userRoleName: string): boolean {
  userRoleName = userRoleName?.toLowerCase();
  return (
    userRoleName === roleNames.customerServiceLogin ||
    userRoleName === roleNames.superAdmin
  );
}

export function isUserDocumentUser(userRoleName: string): boolean {
  userRoleName = userRoleName?.toLowerCase();
  return (
    userRoleName === roleNames.documents ||
    userRoleName === roleNames.admin ||
    isSuperAdmin(userRoleName)
  );
}

export function isDocumentUser(userRoleName: string): boolean {
  userRoleName = userRoleName?.toLowerCase();
  return isUserDocumentUser(userRoleName) || isDocsAdminUser(userRoleName);
}

export function notDocumentUser(userRoleName: string): boolean {
  return userRoleName?.toLowerCase() !== roleNames.documents;
}

/**
 * provede změnu hesla uživatele
 * @param currentPassword aktuální heslo
 * @param newPassword nové heslo
 * @param confirmNewPassword potvrzení nového hesla
 * @param userId ID uživatele
 * @param setLoading funkce, která spouští nebo zastavuje načítání
 * @param setLoading funkce, která se spouští spouští při úspěšném výsledku
 */
export function changePassword(
  currentPassword: string,
  newPassword: string,
  confirmNewPassword: string,
  userId: number,
  setLoading: any,
  loadEntities?: any,
): any {
  if (newPassword === confirmNewPassword) {
    if (newPassword !== currentPassword) {
      patchEntity(
        "user",
        userId,
        {
          password: `${currentPassword};\t;\t;${newPassword}`,
        },
        intl.formatMessage({ id: "user", defaultMessage: "Uživatel" }),
        intl.formatMessage({
          id: "user.errorName",
          defaultMessage: "uživatele",
        }),
        setLoading,
        loadEntities,
      );
      return;
    } else {
      setLoading(false);
      return {
        newPassword: {
          value: newPassword,
          errors: [
            new Error(
              intl.formatMessage({
                id: "session.differentNewPassword",
                defaultMessage: "Nové heslo je shodné se stávajícím heslem!",
              }),
            ),
          ],
        },
      };
    }
  } else {
    setLoading(false);
    return {
      confirmNewPassword: {
        value: confirmNewPassword,
        errors: [
          new Error(
            intl.formatMessage({
              id: "session.differentPassword",
              defaultMessage: "Nové hesla nejsou shodné!",
            }),
          ),
        ],
      },
    };
  }
}
